/**
 * Batch trip map — the map face of the rider popover.
 *
 * Renders, on a single OSM/Leaflet canvas:
 *
 *   - the store(s) involved in the trip (square anchor),
 *   - one numbered customer marker per batched leg, color-coded by
 *     leg index so the popover legend can refer to them, and
 *   - one rider polyline per leg, drawn in the matching leg colour,
 *     stitched from `/staff/api/rider-logs/{order_id}/`.
 *
 * Data comes from existing endpoints (no new BI surface beyond
 * `/api/bi/orders/points/`, which already accepts `order_ids=` after
 * the most recent extension):
 *
 *   - `useOrdersPoints({ orderIds })` → store + customer coords + status,
 *     in one network call.
 *   - `useRiderLogs(orderId)` per leg, fired in parallel only when this
 *     component mounts (i.e. the user has flipped to map mode).
 *
 * The component is intentionally compact — it lives inside a 320–360px
 * popover, so we don't draw legend labels next to every marker; the
 * leg list rendered next to the map is the legend.
 */
import { useEffect, useMemo } from 'react';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

import { useOrdersPoints } from '@/api/bi';
import { useRiderLogs } from '@/api/legacy/orders';
import { Skeleton } from '@/components/ui/Skeleton';
import { fmt } from '@/lib/format';
import { statusLabel } from '@/lib/status';

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// 8-colour qualitative palette — stable per leg index so it lines up
// between the marker, the polyline and the leg list in the popover.
export const LEG_COLORS = [
  '#2563eb', // blue-600
  '#dc2626', // red-600
  '#16a34a', // green-600
  '#d97706', // amber-600
  '#9333ea', // purple-600
  '#0891b2', // cyan-600
  '#db2777', // pink-600
  '#475569', // slate-600
];

function legColor(index: number): string {
  return LEG_COLORS[index % LEG_COLORS.length];
}

function storeIcon(): L.DivIcon {
  return L.divIcon({
    className: 'batch-trip-store',
    html: `<span style="
      display:inline-flex;align-items:center;justify-content:center;
      width:18px;height:18px;border-radius:4px;
      background:#0f172a;color:white;font-size:10px;font-weight:700;
      box-shadow:0 0 0 2px white, 0 1px 3px rgba(0,0,0,.4);
    ">S</span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function FitBounds({ points }: { points: L.LatLngTuple[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [20, 20], maxZoom: 16 });
  }, [map, points]);
  return null;
}

interface LegInfo {
  /** Order id of the leg. */
  orderId: number;
  /** Index in the displayed leg list — drives colour. */
  index: number;
}

export interface BatchTripMapProps {
  /** All legs of the trip in display order (sorted by `sequence`). */
  legs: LegInfo[];
  /** The order id of the row that opened the popover — gets a slightly heavier ring. */
  currentOrderId: number;
  height?: number | string;
  className?: string;
}

export function BatchTripMap({
  legs,
  currentOrderId,
  height = 240,
  className,
}: BatchTripMapProps) {
  const orderIds = useMemo(() => legs.map((l) => l.orderId), [legs]);
  const pointsQuery = useOrdersPoints({ orderIds });

  const ready = !pointsQuery.isLoading && !!pointsQuery.data;

  const points = pointsQuery.data?.points ?? [];
  const stores = pointsQuery.data?.stores ?? [];

  const legByOrderId = useMemo(() => {
    const m = new Map<number, LegInfo>();
    for (const l of legs) m.set(l.orderId, l);
    return m;
  }, [legs]);

  const positions: L.LatLngTuple[] = useMemo(() => {
    const out: L.LatLngTuple[] = [];
    for (const p of points) {
      if (Number.isFinite(p.lat) && Number.isFinite(p.lng)) {
        out.push([p.lat, p.lng]);
      }
    }
    for (const s of stores) {
      if (Number.isFinite(s.latitude) && Number.isFinite(s.longitude)) {
        out.push([s.latitude, s.longitude]);
      }
    }
    return out;
  }, [points, stores]);

  if (!ready) {
    return (
      <div className={className} style={{ height }}>
        <Skeleton className="h-full w-full rounded-md" />
      </div>
    );
  }

  if (positions.length === 0) {
    return (
      <div
        className={className}
        style={{ height }}
      >
        <div className="flex h-full items-center justify-center rounded-md border border-dashed border-border bg-muted/20 px-3 text-center text-[11px] text-muted-foreground">
          No customer or store coordinates available for this trip.
        </div>
      </div>
    );
  }

  const center = positions[0];

  return (
    <div className={className} style={{ height, position: 'relative', isolation: 'isolate', zIndex: 0 }}>
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', borderRadius: 'inherit', position: 'relative', zIndex: 0 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={positions} />

        {stores.map((s) => (
          <Marker
            key={`store-${s.id}`}
            position={[s.latitude, s.longitude]}
            icon={storeIcon()}
            zIndexOffset={500}
          >
            <Popup>
              <div className="text-xs">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Store
                </div>
                <div className="mt-0.5 text-sm font-semibold text-slate-900">
                  {s.name ?? `Store #${s.id}`}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {points.map((p) => {
          const leg = legByOrderId.get(p.id);
          const idx = leg?.index ?? 0;
          const color = legColor(idx);
          const isCurrent = p.id === currentOrderId;
          return (
            <CircleMarker
              key={`order-${p.id}`}
              center={[p.lat, p.lng]}
              radius={isCurrent ? 8 : 6}
              pathOptions={{
                color,
                weight: isCurrent ? 2.5 : 1.5,
                fillColor: color,
                fillOpacity: 0.85,
              }}
            >
              <Popup>
                <div className="text-xs">
                  <div className="kpi-num text-sm font-semibold text-slate-900">
                    #{p.id}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {p.customer_name ?? 'Customer'}
                  </div>
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                    {statusLabel(p.status)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    {fmt.npr(p.final_total)}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}

        {legs.map((leg) => (
          <LegTrail
            key={`trail-${leg.orderId}`}
            orderId={leg.orderId}
            color={legColor(leg.index)}
          />
        ))}
      </MapContainer>
    </div>
  );
}

interface LegTrailProps {
  orderId: number;
  color: string;
}

function LegTrail({ orderId, color }: LegTrailProps) {
  const logs = useRiderLogs(orderId);
  const polyline: L.LatLngTuple[] = useMemo(() => {
    const raw = logs.data?.logs ?? [];
    const cleaned = raw
      .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
      .sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        }
        return 0;
      });
    return cleaned.map((p) => [p.latitude, p.longitude] as L.LatLngTuple);
  }, [logs.data]);

  if (polyline.length < 2) return null;
  return (
    <Polyline
      positions={polyline}
      pathOptions={{ color, weight: 3, opacity: 0.85 }}
    />
  );
}
